<script>
import Layout from '@/views/layouts/main'
import { required } from 'vuelidate/lib/validators'
import PageHeader from '@/components/page-header'
import MultiSelect from '@/components/multi-select'
import i18n from '@/i18n'
import { mapState } from 'vuex'
import { CONFIGURATION_MODULE } from '@/state/modules/configuration'
import { configurationMethods } from '@/state/helpers'

/**
 * Editing one setting.
 *
 * ── The input is chosen by `value_type` ─────────────────────────────────
 * Everything is TEXT in the column, so the type is the only thing that makes
 * an hours field refuse "soon". The server re-validates against the stored
 * type whatever this page sends — the matching input here is a courtesy, not
 * the check.
 *
 * ── Booleans go out as the strings "true"/"false" ───────────────────────
 * Not `true`/`false`, and not "on": the backend normalises what it is given,
 * but the stored value is read back by `filter_var`, and keeping the two
 * spellings it emits ("true"/"false") means the dropdown can match the stored
 * value on load. A real boolean here JSON-encodes to `true`, which is also
 * accepted — so this is about the ROUND TRIP, not about validation.
 */
const BOOLEAN_OPTIONS = () => [
  { text: i18n.t('label.yes'), value: 'true' },
  { text: i18n.t('label.no'), value: 'false' },
]

export default {
  page: { title: i18n.t('label.updateX', [i18n.t('module.configuration')]) },
  components: { Layout, PageHeader, MultiSelect },
  data() {
    return {
      title: i18n.t('label.updateX', [i18n.t('module.configuration')]),
      form: { key: '', label: '', value: '', valueType: 'string' },
      booleanDdl: BOOLEAN_OPTIONS(),
      loading: false,
      submitting: false,
    }
  },
  computed: {
    ...mapState(CONFIGURATION_MODULE, { status: (state) => state.status }),
    breadcrumbItems() {
      return [
        { text: i18n.t('module.admin'), href: '/' },
        { text: i18n.t('module.configuration'), href: '/configurations' },
        { text: this.form.label || this.form.key, active: true },
      ]
    },
    /** Shown under the input so somebody editing `password_reset_valid_hour`
     *  knows it is hours and not minutes. */
    typeHint() {
      return i18n.t(`configuration.hint.${this.form.valueType}`)
    },
  },
  async created() {
    this.loading = true

    try {
      const result = await this.getConfiguration({ key: this.$route.params.key })
      const d = (result && result.data) || {}

      this.form.key = d.key || this.$route.params.key
      this.form.valueType = d.value_type || 'string'
      this.form.label = humanise(this.form.key)

      // Booleans arrive as the stored string. Normalised to exactly
      // "true"/"false" so the dropdown finds its option — a stored "1" or "on"
      // from an older release would otherwise show as blank and read as unset.
      this.form.value =
        this.form.valueType === 'boolean'
          ? truthy(d.value)
            ? 'true'
            : 'false'
          : d.value === null || d.value === undefined
          ? ''
          : String(d.value)
    } finally {
      this.loading = false
    }
  },
  /*
   * The value is required.
   *
   * These are platform-wide settings read by the server — an empty
   * `included_turns_per_month` or `max_login_attempt` is not a cleared field,
   * it is a number the app will try to use. Saving one blank used to go
   * through and the breakage showed up somewhere else entirely.
   *
   * Same shape as every other form: red border, message under the field.
   */
  validations: {
    form: {
      value: { required },
    },
  },

  methods: {
    ...configurationMethods,

    async submit() {
      this.$v.form.$touch()
      if (this.$v.form.$invalid) return

      this.submitting = true

      try {
        const result = await this.updateConfiguration({
          key: this.form.key,
          value: this.form.value,
        })

        if (result) this.$router.push({ name: 'Configurations-listing' })
      } finally {
        this.submitting = false
      }
    },

    cancel() {
      this.$router.push({ name: 'Configurations-listing' })
    },
  },
}

function truthy(value) {
  return ['1', 'true', 'on', 'yes'].includes(String(value).toLowerCase())
}

function humanise(key) {
  if (!key) return ''
  const words = String(key).replace(/\./g, ' · ').split('_').join(' ')
  return words.charAt(0).toUpperCase() + words.slice(1)
}
</script>

<template>
  <Layout>
    <PageHeader :title="title" :items="breadcrumbItems" />
    <div class="row">
      <!-- col-12 like every other screen. The two fields sit side by side
           inside it rather than one stretched input per row. -->
      <div class="col-12">
        <b-alert show variant="warning" class="small d-flex align-items-start">
          <i class="mdi mdi-alert-outline font-size-16 mr-2"></i>
          <span>{{ $t('message.configurationWarning') }}</span>
        </b-alert>

        <div class="card">
          <div class="card-body">
            <b-form @submit.prevent="submit">
              <b-row>
                <b-col cols="12" md="5" lg="4">
              <b-form-group :label="$t('label.key')">
                <!-- Read-only: the key is the identity, not a field. There is
                     no rename because the code reading it would not follow. -->
                <b-form-input :value="form.key" disabled />
                <small class="form-text text-muted">{{ form.label }}</small>
              </b-form-group>
                </b-col>

                <b-col cols="12" md="7" lg="8">
              <b-form-group :label="$t('label.value')">
                <MultiSelect
                  v-if="form.valueType === 'boolean'"
                  v-model="form.value"
                  :options="booleanDdl"
                  :multiple="false"
                  :allow-empty="false"
                  :searchable="false"
                />
                <b-form-input
                  v-else-if="form.valueType === 'integer'"
                  v-model.trim="form.value"
                  type="number"
                  step="1"
                  :state="$v.form.value.$dirty ? !$v.form.value.$error : null"
                />
                <b-form-input
                  v-else-if="form.valueType === 'double'"
                  v-model.trim="form.value"
                  type="number"
                  step="0.01"
                  :state="$v.form.value.$dirty ? !$v.form.value.$error : null"
                />
                <b-form-input
                  v-else-if="form.valueType === 'date'"
                  v-model.trim="form.value"
                  type="date"
                  :state="$v.form.value.$dirty ? !$v.form.value.$error : null"
                />
                <b-form-input
                  v-else-if="form.valueType === 'dateTime'"
                  v-model.trim="form.value"
                  type="datetime-local"
                  :state="$v.form.value.$dirty ? !$v.form.value.$error : null"
                />
                <b-form-textarea
                  v-else-if="form.valueType === 'json'"
                  v-model="form.value"
                  rows="6"
                  :state="$v.form.value.$dirty ? !$v.form.value.$error : null"
                />
                <b-form-input v-else v-model.trim="form.value" type="text" :state="$v.form.value.$dirty ? !$v.form.value.$error : null" />

                <b-form-invalid-feedback :state="$v.form.value.$dirty ? !$v.form.value.$error : null">
                  {{ $t('message.requiredX', [$t('label.value')]) }}
                </b-form-invalid-feedback>
                <small class="form-text text-muted">{{ typeHint }}</small>
              </b-form-group>
                </b-col>
              </b-row>

              <div class="text-right">
                <b-button variant="light" class="mr-2" @click="cancel">{{ $t('label.cancel') }}</b-button>
                <b-button type="submit" variant="primary" :disabled="submitting || loading">
                  {{ $t('label.updateX', [$t('module.configuration')]) }}
                </b-button>
              </div>
            </b-form>
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>
